import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Box, Card, Chip, CircularProgress,
    Alert, Link, Accordion, AccordionSummary, AccordionDetails,
    ToggleButton, ToggleButtonGroup, Tooltip
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    AccountTree as HierarchyIcon,
    OpenInNew as OpenInNewIcon,
    List as ListIcon,
    Share as TreeIcon
} from '@mui/icons-material';

const HierarchyDialog = ({ 
    open, 
    onClose, 
    place, 
    hierarchyData, 
    isLoading, 
    error 
}) => {
    const [expandedLevels, setExpandedLevels] = useState(new Set([0, 1])); // Expand first two levels by default
    const [viewMode, setViewMode] = useState('accordion'); // 'accordion' or 'tree'

    const handleAccordionChange = (level) => (event, isExpanded) => {
        const newExpanded = new Set(expandedLevels);
        if (isExpanded) {
            newExpanded.add(level);
        } else {
            newExpanded.delete(level);
        }
        setExpandedLevels(newExpanded);
    };

    // Group hierarchy data by level/depth (with deduplication)
    const groupedHierarchy = hierarchyData ? (() => {
        // First deduplicate like in tree view
        const uniqueItems = new Map();
        hierarchyData.forEach(item => {
            const existingItem = uniqueItems.get(item.item);
            if (!existingItem || item.level < existingItem.level) {
                uniqueItems.set(item.item, item);
            }
        });

        // Then group by level
        return Array.from(uniqueItems.values()).reduce((acc, item) => {
            const level = item.level || item.depth || 0;
            if (!acc[level]) acc[level] = [];
            acc[level].push(item);
            return acc;
        }, {});
    })() : {};

    const getWikidataUrl = (wikidataId) => {
        const qId = wikidataId.includes('entity/') 
            ? wikidataId.replace('http://www.wikidata.org/entity/', '')
            : wikidataId;
        return `https://www.wikidata.org/wiki/${qId}`;
    };

    const getQId = (wikidataId) => {
        return wikidataId.includes('entity/') 
            ? wikidataId.replace('http://www.wikidata.org/entity/', '')
            : wikidataId;
    };

    const getLevelDescription = (level) => {
        switch(level) {
            case 0: return 'Target Place';
            case 1: return 'Direct Instance Types';
            case 2: return 'Parent Categories';
            case 3: return 'Grandparent Categories';
            case 4: return 'Great-Grandparent Categories';
            case 5: return 'Great-Great-Grandparent Categories';
            default: return `Level ${level} Categories`;
        }
    };

    const getLevelColor = (level) => {
        const colors = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
        return colors[level % colors.length];
    };

    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    // Simple tree node component that shows levels hierarchically
    const TreeNode = ({ item, level, isLastInLevel = false }) => {
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                {/* Indentation and tree lines */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: level * 32 }}>
                    {/* Render indentation lines */}
                    {Array.from({ length: level }, (_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: '32px',
                                height: '60px',
                                borderLeft: index === level - 1 ? '2px solid #e0e0e0' : 'none',
                                borderBottom: index === level - 1 ? '2px solid #e0e0e0' : 'none',
                                borderBottomLeftRadius: index === level - 1 ? '8px' : '0',
                                position: 'relative'
                            }}
                        >
                            {/* Vertical line for parent connection */}
                            {index < level - 1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: '0px',
                                        top: '0px',
                                        width: '2px',
                                        height: '100%',
                                        backgroundColor: '#e0e0e0'
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Node content */}
                <Box sx={{ flex: 1, ml: level === 0 ? 0 : 1 }}>
                    <Card 
                        variant="outlined" 
                        sx={{ 
                            p: 2, 
                            backgroundColor: level === 0 ? 'primary.light' : 'background.paper',
                            color: level === 0 ? 'primary.contrastText' : 'text.primary',
                            border: level === 0 ? '2px solid' : '1px solid',
                            borderColor: level === 0 ? 'primary.main' : 'divider'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip 
                                label={`Level ${level}`}
                                size="small"
                                color={getLevelColor(level)}
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {item.itemLabel}
                            </Typography>
                            <Link
                                href={getWikidataUrl(item.item)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Chip
                                    icon={<OpenInNewIcon sx={{ fontSize: '12px !important' }} />}
                                    label={getQId(item.item)}
                                    size="small"
                                    variant="outlined"
                                    clickable
                                    sx={{ fontSize: '0.65rem', height: '20px' }}
                                />
                            </Link>
                        </Box>
                        
                        {item.instanceOf && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.8, mb: 0.5 }}>
                                <strong>Instance of:</strong> {item.instanceOfLabel}
                            </Typography>
                        )}
                        
                        {item.superclass && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                <strong>Superclass:</strong> {item.superclassLabel}
                            </Typography>
                        )}
                    </Card>
                </Box>
            </Box>
        );
    };

    // Render tree view - deduplicate and show unique items
    const renderTreeView = () => {
        if (!hierarchyData) return null;

        // Deduplicate items - keep the one with the lowest level (most specific)
        const uniqueItems = new Map();
        hierarchyData.forEach(item => {
            const existingItem = uniqueItems.get(item.item);
            if (!existingItem || item.level < existingItem.level) {
                uniqueItems.set(item.item, item);
            }
        });

        // Sort unique items by level, then by name
        const sortedItems = Array.from(uniqueItems.values())
            .sort((a, b) => {
                if (a.level === b.level) {
                    return a.itemLabel.localeCompare(b.itemLabel);
                }
                return a.level - b.level;
            });
        
        console.log('Deduplicated hierarchy items:', sortedItems);
        
        return (
            <Box sx={{ p: 2, overflow: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Items are arranged by hierarchy level, showing the unique classification chain from specific to general.
                </Typography>
                
                {sortedItems.map((item, index) => {
                    const isLastInLevel = index === sortedItems.length - 1 || 
                        sortedItems[index + 1]?.level !== item.level;
                    
                    return (
                        <TreeNode
                            key={`${item.item}-${item.level}`}
                            item={item}
                            level={item.level}
                            isLastInLevel={isLastInLevel}
                        />
                    );
                })}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                pb: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <HierarchyIcon sx={{ color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                        Wikidata Hierarchy Tree
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        {place?.wikidataLabel || 'Unknown Place'} ({getQId(place?.wikidataID || '')})
                    </Typography>
                </Box>
                
                {/* View Mode Toggle */}
                {hierarchyData && !isLoading && (
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        sx={{ ml: 2 }}
                    >
                        <ToggleButton value="accordion">
                            <Tooltip title="List View">
                                <ListIcon />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="tree">
                            <Tooltip title="Tree View">
                                <TreeIcon />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {isLoading && (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200 
                    }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>
                            Loading hierarchy data...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Failed to load hierarchy data: {error}
                        </Typography>
                    </Alert>
                )}

                {hierarchyData && !isLoading && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            This shows the Wikidata classification hierarchy for this place. 
                            {viewMode === 'tree' 
                                ? 'The tree view shows the hierarchical relationships visually.'
                                : 'Each level represents how this place is categorized and what broader categories it belongs to.'
                            }
                        </Typography>

                        {viewMode === 'accordion' ? (
                            // Accordion View
                            <Box>
                                {Object.entries(groupedHierarchy)
                                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                    .map(([level, items]) => (
                                        <Accordion
                                            key={level}
                                            expanded={expandedLevels.has(parseInt(level))}
                                            onChange={handleAccordionChange(parseInt(level))}
                                            sx={{ mb: 1 }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip 
                                                        label={`Level ${level}`}
                                                        size="small"
                                                        color={getLevelColor(parseInt(level))}
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                        {getLevelDescription(parseInt(level))}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ({items.length} item{items.length !== 1 ? 's' : ''})
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {items.map((item, index) => (
                                                        <Card key={index} variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                                            {item.itemLabel}
                                                                        </Typography>
                                                                        <Link
                                                                            href={getWikidataUrl(item.item)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            sx={{ display: 'flex', alignItems: 'center' }}
                                                                        >
                                                                            <Chip
                                                                                icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                                                                                label={getQId(item.item)}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                clickable
                                                                                sx={{ fontSize: '0.7rem' }}
                                                                            />
                                                                        </Link>
                                                                    </Box>
                                                                    
                                                                    {item.instanceOf && (
                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                <strong>Instance of:</strong> {item.instanceOfLabel}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    
                                                                    {item.superclass && (
                                                                        <Box>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                <strong>Superclass:</strong> {item.superclassLabel}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    ))}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                            </Box>
                        ) : (
                            // Tree View
                            renderTreeView()
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button 
                    onClick={onClose}
                    variant="contained"
                    sx={{ borderRadius: 2 }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HierarchyDialog;